import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import api from '../../services/leaseReverseReceiptApi';
import { toApiDate, toOptions } from '../leaseGeneralInvoiceReceipt/formUtils';
import SearchPanel from './SearchPanel';
import ReceiptLinesGrid from './ReceiptLinesGrid';
import AllocationPanel from './AllocationPanel';
import ReversalListGrid from './ReversalListGrid';
import UnallocatedPaymentsGrid from './UnallocatedPaymentsGrid';
import {
    FULL_REVERSE, MSG, buildFullReverse, buildSavePayload, fmt3, findRowsInMessage, listTotal, toListRow, validateAllocation,
} from './reverseHelpers';

const DROPDOWN_TYPES = ['Properties', 'LeaseNos', 'RefundAccounts', 'TransactionTypes'];
const emptyPanel = () => ({ transactionType: '', account: null, amount: '', remarks: '' });
const leaseOption = (leaseNo) => ({ value: leaseNo, label: leaseNo });
const LEAVE_MESSAGE = 'You have unsaved changes. Are you sure you want to leave this page?';

const LeaseReverseReceiptPage = () => {
    // search
    const [dropdowns, setDropdowns] = useState({ properties: [], leaseNos: [], accounts: [], types: [] });
    const [units, setUnits] = useState([]);
    const [property, setProperty] = useState(null);
    const [unit, setUnit] = useState(null);
    const [leaseNo, setLeaseNo] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [receiptNo, setReceiptNo] = useState('');
    const [totalPaid, setTotalPaid] = useState(null);
    const [loading, setLoading] = useState({ dropdowns: true, units: false, leaseNos: false, lines: false, unallocated: false });

    // grids
    const [lines, setLines] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [unallocated, setUnallocated] = useState([]);
    const [panel, setPanel] = useState(emptyPanel());
    const [list, setList] = useState([]);
    const [reverseDate, setReverseDate] = useState(new Date());
    const [submitError, setSubmitError] = useState('');
    const [highlightIds, setHighlightIds] = useState([]);

    // flow
    const [confirm, setConfirm] = useState(null); // 'full' | 'reset' | 'save'
    const [saving, setSaving] = useState(false);
    const [updatingRemarks, setUpdatingRemarks] = useState(false);

    // Guards against out-of-order responses when the user changes the lease quickly.
    const linesRequest = useRef(0);
    const unallocatedRequest = useRef(0);
    const lastReceiptSearch = useRef('');
    const savingRef = useRef(false);

    const setLoad = (patch) => setLoading((prev) => ({ ...prev, ...patch }));
    const failed = (res) => toast.error(res.message);

    const selectedLine = useMemo(() => lines.find((l) => `${l.recordId}` === `${selectedId}`) || null, [lines, selectedId]);
    const inListIds = useMemo(() => new Set(list.map((r) => `${r.recordId}`)), [list]);
    const remarksDirty = unallocated.some((r) => r.remarks !== r.originalRemarks);
    const hasUnsaved = list.length > 0 || remarksDirty;
    const busy = saving;

    /* ---------------- loaders ---------------- */

    const loadLeaseNos = useCallback(async (all) => {
        setLoad({ leaseNos: true });
        const res = await api.getDropdowns(['LeaseNos'], all);
        setLoad({ leaseNos: false });
        if (!res.ok) return failed(res);
        setDropdowns((prev) => ({ ...prev, leaseNos: toOptions(res.dropdowns?.LeaseNos) }));
    }, []);

    const loadUnits = useCallback(async (propertyNo, all) => {
        setLoad({ units: true });
        const res = await api.getUnits({ propertyNo, showAll: all });
        setLoad({ units: false });
        if (!res.ok) { setUnits([]); return failed(res); }
        setUnits((res.result || []).map((u) => ({ value: u.unitNo, label: u.unitNo, leaseNo: u.leaseNo })));
    }, []);

    const loadLines = useCallback(async (lease, receipt) => {
        const request = ++linesRequest.current;
        if (!lease && !`${receipt || ''}`.trim()) {
            setLines([]); setTotalPaid(null); setSelectedId(null);
            setLoad({ lines: false });
            return;
        }
        setLoad({ lines: true });
        const res = await api.getReceiptLines({ leaseNo: lease, receiptNo: `${receipt || ''}`.trim() });
        if (request !== linesRequest.current) return;
        setLoad({ lines: false });
        setSelectedId(null);
        if (!res.ok) { setLines([]); setTotalPaid(null); return failed(res); }
        setLines(res.result?.lines || []);
        setTotalPaid(res.result?.totalPaid ?? null);
    }, []);

    const loadUnallocated = useCallback(async (lease) => {
        const request = ++unallocatedRequest.current;
        if (!lease) { setUnallocated([]); setLoad({ unallocated: false }); return; }
        setLoad({ unallocated: true });
        const res = await api.getUnallocatedPayments(lease);
        if (request !== unallocatedRequest.current) return;
        setLoad({ unallocated: false });
        if (!res.ok) { setUnallocated([]); return failed(res); }
        setUnallocated((res.result || []).map((r) => ({ ...r, remarks: r.remarks ?? '', originalRemarks: r.remarks ?? '' })));
    }, []);

    useEffect(() => {
        (async () => {
            const res = await api.getDropdowns(DROPDOWN_TYPES, false);
            setLoad({ dropdowns: false });
            if (!res.ok) return failed(res);
            const d = res.dropdowns || {};
            setDropdowns({
                properties: toOptions(d.Properties),
                leaseNos: toOptions(d.LeaseNos),
                accounts: toOptions(d.RefundAccounts, (i) => `${i.value} - ${i.text}`),
                types: toOptions(d.TransactionTypes),
            });
        })();
    }, []);

    /* ---------------- leaving the page ---------------- */

    useEffect(() => {
        if (!hasUnsaved) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        // In-app links (the sidebar) never trigger beforeunload, so intercept them in the capture phase.
        const onClick = (e) => {
            const anchor = e.target.closest?.('a[href]');
            if (!anchor || anchor.target === '_blank' || !anchor.getAttribute('href').startsWith('/')) return;
            if (!window.confirm(LEAVE_MESSAGE)) { e.preventDefault(); e.stopPropagation(); }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClick, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClick, true);
        };
    }, [hasUnsaved]);

    /* ---------------- search handlers ---------------- */

    const handleLeaseChange = (option) => {
        setLeaseNo(option || null);
        loadLines(option?.value, receiptNo);
        loadUnallocated(option?.value);
    };

    const handleProperty = (option) => {
        setProperty(option || null);
        setUnit(null);
        setUnits([]);
        if (option) loadUnits(option.value, showAll);
    };

    const handleUnit = (option) => {
        setUnit(option || null);
        if (option?.leaseNo) handleLeaseChange(leaseOption(option.leaseNo));
    };

    const handleShowAll = (checked) => {
        setShowAll(checked);
        loadLeaseNos(checked);
        if (property) loadUnits(property.value, checked);
    };

    const handleReceiptNoCommit = () => {
        const receipt = receiptNo.trim();
        if (!receipt) return;
        // Enter followed by the blur it causes must not search twice.
        const key = `${leaseNo?.value || ''}|${receipt}`;
        if (lastReceiptSearch.current === key && !loading.lines) return;
        lastReceiptSearch.current = key;
        loadLines(leaseNo?.value, receipt);
    };

    const handleReceiptNoChange = (value) => {
        lastReceiptSearch.current = '';
        setReceiptNo(value);
    };

    const handleSelectLine = (line) => {
        setSelectedId(line.recordId);
        setPanel((p) => ({ ...p, amount: `${line.amountPaid}` }));
    };

    /* ---------------- allocation ---------------- */

    const patchPanel = (patch) => setPanel((p) => ({ ...p, ...patch }));
    const clearPanel = () => { setPanel(emptyPanel()); setSelectedId(null); };

    const applyFullReverse = () => {
        setList(buildFullReverse(lines, panel.remarks, panel.account));
        setSubmitError('');
        setHighlightIds([]);
        clearPanel();
        setConfirm(null);
    };

    const handleAllocate = () => {
        setSubmitError('');
        if (panel.transactionType === FULL_REVERSE) {
            if (!panel.remarks.trim()) return toast.warn(MSG.enterRemarks);
            if (lines.length === 0) return toast.warn(MSG.noLines);
            if (list.length > 0) return setConfirm('full');
            return applyFullReverse();
        }
        const error = validateAllocation(panel, selectedLine, list);
        if (error) {
            toast.warn(error);
            if (error === MSG.unclearedCheque) setPanel((p) => ({ ...p, transactionType: '', amount: '', remarks: '' }));
            return undefined;
        }
        setList((prev) => [...prev, toListRow(selectedLine, {
            transactionType: panel.transactionType, amount: panel.amount, remarks: panel.remarks, account: panel.account,
        })]);
        setPanel((p) => ({ ...p, transactionType: '', amount: '', remarks: '' }));
        setSelectedId(null);
        return undefined;
    };

    const handleReset = () => {
        if (list.length > 0) return setConfirm('reset');
        return clearPanel();
    };

    const applyReset = () => {
        setList([]);
        setSubmitError('');
        setHighlightIds([]);
        clearPanel();
        setConfirm(null);
    };

    const handleRemove = (recordId) => {
        setList((prev) => prev.filter((r) => `${r.recordId}` !== `${recordId}`));
        setHighlightIds((prev) => prev.filter((id) => `${id}` !== `${recordId}`));
    };

    /* ---------------- save ---------------- */

    const handleSaveClick = () => {
        if (list.length === 0) return toast.warn(MSG.listEmpty);
        if (!reverseDate) return toast.warn(MSG.dateRequired);
        return setConfirm('save');
    };

    const handleConfirmSave = async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setConfirm(null);
        setSubmitError('');
        setHighlightIds([]);
        const res = await api.reverseReceipts(buildSavePayload(list, toApiDate(reverseDate)));
        savingRef.current = false;
        setSaving(false);
        if (!res.ok) {
            setSubmitError(res.message);
            setHighlightIds(findRowsInMessage(list, res.message));
            return;
        }
        const { rrNo, linesReversed, totalAmount } = res.result || {};
        toast.success(`Successfully saved. RR No: ${rrNo} (${linesReversed} lines, ${fmt3(totalAmount ?? listTotal(list))})`);
        setList([]);
        clearPanel();
        loadLines(leaseNo?.value, receiptNo);
        loadUnallocated(leaseNo?.value);
    };

    /* ---------------- unallocated remarks ---------------- */

    const handleRemarksChange = (unallocatedId, remarks) =>
        setUnallocated((prev) => prev.map((r) => (r.unallocatedId === unallocatedId ? { ...r, remarks } : r)));

    const handleUpdateRemarks = async () => {
        const dirty = unallocated.filter((r) => r.remarks !== r.originalRemarks);
        if (dirty.length === 0) return;
        setUpdatingRemarks(true);
        const res = await api.updateUnallocatedRemarks({
            leaseNo: leaseNo?.value || dirty[0].leaseNo,
            items: dirty.map((r) => ({ unallocatedId: r.unallocatedId, remarks: r.remarks })),
        });
        setUpdatingRemarks(false);
        if (!res.ok) return failed(res); // keep the edits
        toast.success('Successfully Updated');
        setUnallocated((prev) => prev.map((r) => ({ ...r, originalRemarks: r.remarks })));
    };

    /* ---------------- render ---------------- */

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between border-box2 p-3" style={{ borderRadius: 8 }}>
                <h6 className="h6 mb-0">Lease Receipt Reverse</h6>
            </div>

            <SearchPanel
                properties={dropdowns.properties}
                units={units}
                leaseNos={dropdowns.leaseNos}
                property={property}
                unit={unit}
                leaseNo={leaseNo}
                showAll={showAll}
                receiptNo={receiptNo}
                totalPaid={totalPaid}
                loading={loading}
                disabled={busy}
                onProperty={handleProperty}
                onUnit={handleUnit}
                onLeaseNo={handleLeaseChange}
                onShowAll={handleShowAll}
                onReceiptNoChange={handleReceiptNoChange}
                onReceiptNoCommit={handleReceiptNoCommit}
            />

            <ReceiptLinesGrid
                lines={lines}
                loading={loading.lines}
                selectedId={selectedId}
                inListIds={inListIds}
                onSelect={handleSelectLine}
            />

            <AllocationPanel
                panel={panel}
                selectedLine={selectedLine}
                typeOptions={dropdowns.types}
                accountOptions={dropdowns.accounts}
                disabled={busy}
                onChange={patchPanel}
                onAllocate={handleAllocate}
                onReset={handleReset}
            />

            <ReversalListGrid
                list={list}
                error={submitError}
                highlightIds={highlightIds}
                reverseDate={reverseDate}
                saving={saving}
                onRemove={handleRemove}
                onDateChange={setReverseDate}
                onSave={handleSaveClick}
            />

            <UnallocatedPaymentsGrid
                rows={unallocated}
                loading={loading.unallocated}
                updating={updatingRemarks}
                disabled={busy}
                onRemarksChange={handleRemarksChange}
                onUpdate={handleUpdateRemarks}
            />

            <Modal show={confirm === 'full'} onHide={() => setConfirm(null)} backdrop="static" size="sm">
                <Modal.Header closeButton><Modal.Title>Confirm</Modal.Title></Modal.Header>
                <Modal.Body>Full Reverse will replace the current list with all receipt lines shown. Continue?</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" onClick={() => setConfirm(null)}>No</button>
                    <button type="button" className="mainbtnRed" onClick={applyFullReverse}>Yes</button>
                </Modal.Footer>
            </Modal>

            <Modal show={confirm === 'reset'} onHide={() => setConfirm(null)} backdrop="static" size="sm">
                <Modal.Header closeButton><Modal.Title>Confirm</Modal.Title></Modal.Header>
                <Modal.Body>Clear the reversal list?</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" onClick={() => setConfirm(null)}>No</button>
                    <button type="button" className="mainbtnRed" onClick={applyReset}>Yes</button>
                </Modal.Footer>
            </Modal>

            <Modal show={confirm === 'save'} onHide={() => setConfirm(null)} backdrop="static" size="sm">
                <Modal.Header closeButton><Modal.Title>Confirm</Modal.Title></Modal.Header>
                <Modal.Body>This option will reverse the receipts in the list. Proceed?</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" onClick={() => setConfirm(null)}>No</button>
                    <button type="button" className="mainbtnRed" onClick={handleConfirmSave}>Yes</button>
                </Modal.Footer>
            </Modal>

            {saving && (
                <div
                    role="alert"
                    aria-busy="true"
                    style={{
                        position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(0,0,0,0.55)', color: '#fff',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
                    }}
                >
                    <ClipLoader color="#ffffff" size={48} />
                    <div>Reversing receipts, please wait…</div>
                </div>
            )}
        </div>
    );
};

export default LeaseReverseReceiptPage;
